import React, { useState, useEffect, useRef } from 'react';
import { Bell, Check, CheckCheck, X, ExternalLink, Crown, TrendingUp, Award, Activity, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
    level_up: Crown,
    ranking_change: TrendingUp,
    verification: Award,
    milestone: Sparkles,
    system: Activity,
};
const CATEGORY_COLORS = {
    level_up: 'text-yellow-400',
    ranking_change: 'text-cyan-400',
    verification: 'text-green-400',
    milestone: 'text-purple-400',
    system: 'text-blue-400',
};

const NotificationBell = () => {
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);
    const wsRef = useRef(null);

    useEffect(() => {
        loadUnreadCount();
        // Poll for new notifications every 30 seconds (fallback)
        const interval = setInterval(loadUnreadCount, 30000);
        // Subscribe to federation alerts for real-time updates
        connectAlertsWs();
        return () => {
            clearInterval(interval);
            wsRef.current?.close();
        };
    }, []);

    const connectAlertsWs = () => {
        if (!localStorage.getItem('token')) return;
        const wsUrl = API_URL.replace(/^https/, 'wss').replace(/^http/, 'ws');
        try {
            const ws = new WebSocket(`${wsUrl}/api/federation-alerts/ws`);
            wsRef.current = ws;
            ws.onmessage = (msg) => {
                try {
                    const data = JSON.parse(msg.data);
                    if (data.type === 'new_alert') {
                        // A new alert was broadcast → notification may have been fanned out for this user.
                        // Don't optimistically bump (user may not be in target_user_ids); refetch instead.
                        loadUnreadCount();
                        // Also refresh list if dropdown is open
                        if (isOpen) {
                            setTimeout(loadNotifications, 500);
                        }
                    }
                } catch (e) {}
            };
        } catch (e) {}
    };

    useEffect(() => {
        // Close dropdown when clicking outside
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const loadUnreadCount = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const response = await axios.get(`${API_URL}/api/notifications/unread-count`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUnreadCount(response.data.unread_count || 0);
        } catch (error) {
            console.error('Failed to load unread count:', error);
        }
    };

    const loadNotifications = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const response = await axios.get(`${API_URL}/api/notifications?limit=10`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setNotifications(response.data.notifications || []);
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = () => {
        if (!isOpen) {
            loadNotifications();
        }
        setIsOpen(!isOpen);
    };

    const markAsRead = async (notificationId) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/notifications/${notificationId}/read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setNotifications(prev => 
                prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    const markAllAsRead = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API_URL}/api/notifications/mark-all-read`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            setUnreadCount(0);
        } catch (error) {
            console.error('Failed to mark all as read:', error);
        }
    };

    const handleNotificationClick = (notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
        if (notification.link) {
            // Internal links use same-tab navigation; external links open in new tab
            const isInternal = notification.link.startsWith('/');
            if (isInternal) {
                window.location.href = notification.link;
            } else {
                window.open(notification.link, '_blank');
            }
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'urgent': return 'border-l-red-500';
            case 'high': return 'border-l-amber-500';
            case 'normal': return 'border-l-blue-500';
            default: return 'border-l-gray-500';
        }
    };

    const formatTimeAgo = (dateStr) => {
        const date = new Date(dateStr);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);
        
        if (diff < 60) return 'Just now';
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
        return date.toLocaleDateString();
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                variant="ghost"
                size="sm"
                onClick={handleToggle}
                className="relative p-2 text-muted-foreground hover:text-white"
                data-testid="notification-bell"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </Button>

            {isOpen && (
                <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-[#0d2847] border border-white/10 rounded-lg shadow-xl z-50 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between p-3 border-b border-white/10">
                        <h3 className="font-bold text-sm uppercase tracking-wider">Notifications</h3>
                        {unreadCount > 0 && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={markAllAsRead}
                                className="text-xs text-primary hover:text-primary h-auto py-1"
                            >
                                <CheckCheck className="w-3 h-3 mr-1" />
                                Mark all read
                            </Button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-96 overflow-y-auto">
                        {loading ? (
                            <div className="p-4 text-center text-muted-foreground text-sm">
                                Loading...
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="p-8 text-center text-muted-foreground">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        ) : (
                            notifications.map((notif) => {
                                const Icon = CATEGORY_ICONS[notif.category] || Bell;
                                const iconColor = CATEGORY_COLORS[notif.category] || 'text-blue-400';
                                return (
                                <div
                                    key={notif.id}
                                    onClick={() => handleNotificationClick(notif)}
                                    className={`p-3 border-b border-white/5 cursor-pointer transition-colors hover:bg-white/5 border-l-4 ${getPriorityColor(notif.priority)} ${
                                        !notif.read ? 'bg-primary/5' : ''
                                    }`}
                                    data-testid={`bell-notif-${notif.id}`}
                                >
                                    <div className="flex items-start gap-2">
                                        <div className={`p-1.5 rounded bg-white/5 flex-shrink-0`}>
                                            <Icon className={`w-3.5 h-3.5 ${iconColor}`} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <p className={`text-sm font-medium truncate ${!notif.read ? 'text-white' : 'text-muted-foreground'}`}>
                                                    {notif.title}
                                                </p>
                                                {!notif.read && (
                                                    <span className="w-2 h-2 bg-primary rounded-full flex-shrink-0" />
                                                )}
                                            </div>
                                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                {notif.message}
                                            </p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className="text-xs text-muted-foreground">
                                                    {formatTimeAgo(notif.created_at)}
                                                </span>
                                                {notif.category && notif.category !== 'system' && (
                                                    <span className={`text-[10px] px-1.5 py-0.5 rounded bg-white/5 ${iconColor}`}>
                                                        {notif.category.replace('_', ' ')}
                                                    </span>
                                                )}
                                                {notif.link && (
                                                    <ExternalLink className="w-3 h-3 text-primary" />
                                                )}
                                            </div>
                                        </div>
                                        {notif.image_url && (
                                            <img 
                                                src={notif.image_url} 
                                                alt="" 
                                                className="w-12 h-12 rounded object-cover flex-shrink-0"
                                            />
                                        )}
                                    </div>
                                </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="p-2 border-t border-white/10 text-center">
                            <a 
                                href="/notifications" 
                                className="text-xs text-primary hover:underline"
                            >
                                View all notifications
                            </a>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
